import { SceneAvailability, type SceneGroup } from '@/components/SceneSelection/types'

import type {
  CartBundleStorefrontDto,
  CatalogBundleStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import { getSceneRefFromAttributes } from './sceneSelectionMapper'

/**
 * Scene IDs that can be executed at a school roadshow. Includes the four
 * user-selectable roadshow scenes (Timeless, Emerge, Living Walls, Exposure)
 * plus Yearbook, which is auto-included on every roadshow session.
 *
 * Front-end source of truth for roadshow compatibility — the CADY Core API's
 * per-`LocationModel` `AvailableScenes` array is unreliable, so this allowlist
 * replaces it for the scene-downgrade trigger and the in-drawer scene clamp.
 *
 * Adjacent constant: [`RS_PLUS_SCHOOL_SCENE_IDS`](./roadshowUpgrades.ts) is the
 * subset offered in the RS+ school scene picker (4 scenes, no Yearbook). They
 * are intentionally distinct: this list answers "is this scene compatible
 * with a roadshow?", not "is this scene shown in the RS+ picker?".
 */
export const ROADSHOW_COMPATIBLE_SCENE_IDS = [
  'yearbook',
  'timeless',
  'emerge',
  'living-walls',
  'exposure'
] as const

export const ROADSHOW_COMPATIBLE_SCENE_ID_SET: ReadonlySet<string> = new Set(
  ROADSHOW_COMPATIBLE_SCENE_IDS
)

/** True if `sceneId` is in `ROADSHOW_COMPATIBLE_SCENE_IDS`. */
export function isRoadshowCompatibleScene(sceneId: string): boolean {
  return ROADSHOW_COMPATIBLE_SCENE_ID_SET.has(sceneId)
}

/**
 * Returns the required scene count for a bundle's "User Selected Scene" group.
 * Reads `minRequiredItems` from the first non-included item group.
 * Returns 0 if no such group exists.
 */
export function getRequiredSceneCount(dto: CatalogBundleStorefrontDto): number {
  const sceneGroup = dto.bundle?.itemGroups.find((g) => !g.isIncluded)
  return sceneGroup?.minRequiredItems ?? 0
}

/**
 * Returns the maximum allowed scenes for a bundle's "User Selected Scene" group.
 * Reads `maxAllowedItems` from the first non-included item group.
 * Returns 0 if no such group exists.
 */
export function getMaxSceneCount(dto: CatalogBundleStorefrontDto): number {
  const sceneGroup = dto.bundle?.itemGroups.find((g) => !g.isIncluded)
  return sceneGroup?.maxAllowedItems ?? 0
}

/**
 * Reconcile scene selections against a new session type's available scenes.
 * Keeps selected scenes that are still Selectable or Included in the new tier,
 * drops those that aren't, and trims to the new tier's max if needed.
 *
 * @param currentSceneIds - The user's currently selected scene IDs.
 * @param newSceneGroups - Scene groups from the new session type's catalog data.
 * @param maxScenes - Maximum allowed scenes for the new session type (0 = no limit).
 * @returns The reconciled scene ID array.
 */
export function reconcileSceneSelections(
  currentSceneIds: string[],
  newSceneGroups: SceneGroup[] | undefined,
  maxScenes: number
): string[] {
  if (!newSceneGroups || currentSceneIds.length === 0) return []
  const availableIds = new Set<string>()
  for (const group of newSceneGroups) {
    for (const item of group.items) {
      if (
        item.availability === SceneAvailability.Selectable ||
        item.availability === SceneAvailability.Included
      ) {
        availableIds.add(item.sceneId)
      }
    }
  }
  const reconciled = currentSceneIds.filter((id) => availableIds.has(id))
  if (maxScenes > 0 && reconciled.length > maxScenes) {
    return reconciled.slice(0, maxScenes)
  }
  return reconciled
}

/**
 * Returns cart-bundle IDs of non-session bundles whose `cartItems` reference
 * scenes outside the allowed set. Companion to `reconcileSceneSelections` —
 * that one operates on the session bundle's `selectedItems`; this one
 * operates on add-on / upsell bundles' `cartItems`.
 *
 * The `CADY_SceneId` attribute on a cartItem is the canonical signal that
 * the item is scene-shaped. Items without it (retouching, digital images,
 * prepay) are non-scene and never marked for removal here.
 *
 * @param cartBundles            - `cartStore.cartBundles`.
 * @param sessionCatalogBundleId - Current session bundle's catalogBundleId; skipped.
 * @param allowedSceneIds        - Front-end scene IDs permitted in the new context
 *                                 (e.g. `ROADSHOW_COMPATIBLE_SCENE_ID_SET` for school).
 */
export function findSceneIncompatibleAddOnBundleIds(
  cartBundles: ReadonlyArray<CartBundleStorefrontDto>,
  sessionCatalogBundleId: string,
  allowedSceneIds: ReadonlySet<string>
): string[] {
  const orphans: string[] = []
  for (const cb of cartBundles) {
    // Never sweep the session bundle itself — its scene selectedItems are
    // reconciled separately by reconcileSceneSelections, and removing it
    // would orphan the entire booking.
    if (cb.catalogBundleId === sessionCatalogBundleId) continue
    // One incompatible scene-shaped item invalidates the whole bundle, so
    // .some() short-circuits on the first hit. cartItems is defensively
    // coalesced because the API DTO marks it optional.
    const incompatible = (cb.cartItems ?? []).some((ci) => {
      // getSceneRefFromAttributes reads CADY_SceneId — returns null for any
      // cartItem that isn't scene-shaped (retouching, digital images, prepay,
      // hair/makeup item identifiers, etc.).
      const ref = getSceneRefFromAttributes(ci.item?.attributes)
      // Scene-shaped items: flag if outside the allowed set. Non-scene items
      // pass through untouched — their validity is enforced by Layer 1's
      // catalog-id sweep, not by this scene check.
      return ref ? !allowedSceneIds.has(ref.sceneId) : false
    })
    // Push cartBundleId (the deletion key), not catalogBundleId — they're
    // distinct: catalogBundleId identifies the SKU, cartBundleId identifies
    // this user's specific cart line.
    if (incompatible) orphans.push(cb.cartBundleId)
  }
  return orphans
}
